import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import './retro.css';
import App from './App';
import { AuthProvider } from './context/AuthContext';
import { UserStatsProvider } from './context/UserStatsContext';
import { SubscriptionProvider } from './context/SubscriptionContext';
import { ThemeProvider } from './context/ThemeContext';
import ErrorBoundary from './components/ErrorBoundary';
import { setupGlobalErrorHandlers } from './services/setupErrorHandlers';
import { getVersionString } from './version';
import reportWebVitals from './reportWebVitals';

// Redirect from old domain to new domain
if (window.location.hostname === 'pronunciation-ui.vercel.app') {
  window.location.replace(`https://litrlly.app${window.location.pathname}${window.location.search}`);
}

// Setup global error handlers
setupGlobalErrorHandlers();

// Log version for debugging
console.log(`Litrlly ${getVersionString()}`);

// Patch DOM methods to prevent crashes from third-party scripts (Google Ads, GTM)
// that inject/remove nodes inside React's managed tree.
if (typeof Node.prototype.removeChild === 'function') {
  const originalRemoveChild = Node.prototype.removeChild;
  Node.prototype.removeChild = function <T extends Node>(child: T): T {
    if (child.parentNode !== this) {
      console.warn('removeChild: node is not a child of this parent — suppressed (likely third-party script).');
      return child;
    }
    return originalRemoveChild.call(this, child) as T;
  };
}
if (typeof Node.prototype.insertBefore === 'function') {
  const originalInsertBefore = Node.prototype.insertBefore;
  Node.prototype.insertBefore = function <T extends Node>(newNode: T, refNode: Node | null): T {
    if (refNode && refNode.parentNode !== this) {
      console.warn('insertBefore: ref node is not a child of this parent — suppressed (likely third-party script).');
      return newNode;
    }
    return originalInsertBefore.call(this, newNode, refNode) as T;
  };
}

const rootElement = document.getElementById('root');
if (!rootElement) {
  throw new Error('Root element not found');
}

const root = ReactDOM.createRoot(rootElement);
root.render(
  <ErrorBoundary>
    <ThemeProvider>
      <AuthProvider>
        <SubscriptionProvider>
          <UserStatsProvider>
            <App />
          </UserStatsProvider>
        </SubscriptionProvider>
      </AuthProvider>
    </ThemeProvider>
  </ErrorBoundary>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
